package one.healthium.notifications.scheduler

import co.touchlab.kermit.Logger
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toInstant
import one.healthium.base.utils.toLocalDateTime
import one.healthium.notifications.manager.Notification
import one.healthium.notifications.manager.NotificationManager
import kotlin.time.Clock
import kotlin.time.Duration.Companion.hours
import kotlin.time.Instant

class FCMNotificationProcessor(
    private val notificationScheduler: NotificationScheduler,
    private val notificationManager: NotificationManager,
) {
    private val logger = Logger.withTag("FCMNotificationProcessor")

    private val scope = CoroutineScope(Dispatchers.Default + SupervisorJob())

    fun processAndSchedule(data: Map<Any, Any>, notHandled: () -> Unit = {}) {
        scope.launch {
            logger.i("Processing notification data: $data")
            val notification = buildNotification(data)
            if (notification == null) {
                logger.e("notification not handled")
                notHandled()
                return@launch
            }

            val scheduleAt = (data["scheduleAt"] as? String)?.toInstant()
            if (scheduleAt != null) {
                logger.i("Scheduling notification at $scheduleAt: $notification")
                notificationScheduler.schedule(scheduleAt, notification)
            } else {
                logger.i("Showing notification immediately: $notification")
                notificationManager.showNotification(notification)
            }
        }
    }

    private fun buildNotification(data: Map<Any, Any>): Notification? {
        val title = data["title"] as? String ?: return null
        val body = data["body"] as? String ?: return null
        val link = data["deepLink"] as? String
        val imageFilePath = data["image"] as? String

        return Notification(
            id = generateNotificationId("FCM"),
            title = title,
            body = body,
            link = link,
            imagePath = imageFilePath,
        )
    }

    private fun String.toInstant(): Instant? {
        return this.split(":").let {
            val hours = it.getOrNull(0)?.toIntOrNull()
            val minutes = it.getOrNull(1)?.toIntOrNull()
            if (hours != null && minutes != null) {
                val now = Clock.System.now()
                now.toLocalDateTime().let { dateTime ->
                    val scheduledDateTime = LocalDateTime(
                        year = dateTime.year,
                        month = dateTime.month.number,
                        day = dateTime.day,
                        hour = hours,
                        minute = minutes,
                        second = 0,
                        nanosecond = 0
                    )

                    scheduledDateTime
                        .toInstant(TimeZone.currentSystemDefault())
                        .let { instant ->
                            if (instant < now) instant.plus(24.hours)
                            else instant
                        }
                }
            } else {
                null
            }
        }
    }

    // Deterministic 16-bit FNV-1a hash for stable cross-platform prefix hashing
    private fun fnv1a16(input: String): Int {
        var hash = 0x811c9dc5.toInt()
        val bytes = input.encodeToByteArray()
        for (b in bytes) {
            val v = b.toInt() and 0xFF
            hash = (hash xor v) * 0x01000193
        }
        return hash and 0xFFFF
    }

    /**
     * Generate a 32-bit notification id which encodes a short prefix in the high 16 bits
     * and uses low 16 bits from the current timestamp. This reduces collisions across different
     * producers that use distinct prefixes. It's fast and works on commonMain.
     */
    private fun generateNotificationId(prefix: String): Int {
        // Use deterministic FNV-1a 16-bit prefix hash in the high part
        val prefixHash = fnv1a16(prefix)
        // Use low 16 bits of current time in milliseconds
        val nowLow = (Clock.System.now().toEpochMilliseconds() and 0xFFFF).toInt()
        return (prefixHash shl 16) or nowLow
    }
}